// Landing Page - Main Entry Point with Performance Optimizations
import { lazy, Suspense, useEffect } from 'react';
import Navigation from './components/Navigation/Navigation';
import HeroSection from './components/HeroSection/HeroSection';

// Load critical CSS first for above-the-fold content
import './styles/critical.css';
// Defer non-critical CSS
import './styles/LandingPage.css';

// Lazy load below-the-fold components for better performance
const FeaturesSection = lazy(() => import('./components/FeaturesSection/FeaturesSection'));
const TargetAudienceSection = lazy(() => import('./components/TargetAudienceSection/TargetAudienceSection'));
const HowItWorksSection = lazy(() => import('./components/HowItWorksSection/HowItWorksSection'));
const TestimonialsSection = lazy(() => import('./components/TestimonialsSection/TestimonialsSection'));
const Footer = lazy(() => import('./components/Footer/Footer'));
const WhatsAppWidget = lazy(() => import('./components/WhatsAppWidget/WhatsAppWidget'));

// Loading skeleton component for better UX
const SectionLoader = () => (
  <div className="w-full py-16 flex items-center justify-center">
    <div className="animate-pulse space-y-4 w-full max-w-5xl px-4">
      <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto"></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    </div>
  </div>
);

export default function LandingPage() {
  // Sync dark/light class on <html> with the stored app theme preference.
  // The dashboard ThemeProvider saves to localStorage 'themeSettings'.
  // This makes all .dark CSS selectors work on the public landing page.
  useEffect(() => {
    const applyTheme = (mediaPrefersDark: boolean) => {
      let saved: { theme?: string } = {};
      try {
        const raw = localStorage.getItem('themeSettings');
        if (raw) saved = JSON.parse(raw);
      } catch { /* ignore */ }
      const stored = saved?.theme ?? 'auto';
      const isDark = stored === 'dark' || (stored === 'auto' && mediaPrefersDark);
      const root = document.documentElement;
      root.classList.remove('dark', 'light');
      root.classList.add(isDark ? 'dark' : 'light');
    };
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    applyTheme(mq.matches);
    const handleChange = (e: MediaQueryListEvent) => applyTheme(e.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  return (
    <div className="landing-page">
      {/* Critical above-the-fold content - loaded immediately */}
      <Navigation />
      
      <main id="main-content">
        {/* Critical above-the-fold content - loaded immediately */}
        <HeroSection />
        
        {/* Below-the-fold content - lazy loaded */}
        <Suspense fallback={<SectionLoader />}>
          <FeaturesSection />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <TargetAudienceSection />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <HowItWorksSection />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <TestimonialsSection />
        </Suspense>
      </main>
      
      <Suspense fallback={<SectionLoader />}>
        <Footer />
      </Suspense>

      {/* Floating WhatsApp Widget - lazy loaded */}
      <Suspense fallback={null}>
        <WhatsAppWidget />
      </Suspense>
    </div>
  );
}
